import { useEffect, useState } from 'react';
import {
  HouseholdItem, PEOPLE, Person, Project,
  Recurrence,
  RECURRENCE_LABELS,
  Status,
  STATUS_LABELS,
} from './types';

interface ItemDialogProps {
  item?: HouseholdItem;
  projects: Project[];
  defaultProjectId?: string;
  onClose: () => void;
  // eslint-disable-next-line no-unused-vars
  onSave: (values: {
    title: string;
    description: string;
    status: Status;
    assignee?: Person;
    dueDate?: string;
    effort?: number;
    recurrence: Recurrence;
    projectId?: string;
  }) => void;
}

function ItemDialog({
  item, projects, defaultProjectId, onClose, onSave,
}: ItemDialogProps) {
  const [title, setTitle] = useState(item?.title ?? '');
  const [description, setDescription] = useState(item?.description ?? '');
  const [status, setStatus] = useState<Status>(item?.status ?? 'todo');
  const [assignee, setAssignee] = useState<Person | ''>(item?.assignee ?? '');
  const [dueDate, setDueDate] = useState(item?.dueDate ?? '');
  const [effort, setEffort] = useState<string>(item?.effort ? String(item.effort) : '');
  const [recurrence, setRecurrence] = useState<Recurrence>(item?.recurrence ?? 'none');
  const [projectId, setProjectId] = useState(item?.projectId ?? defaultProjectId ?? '');
  const [titleError, setTitleError] = useState(false);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleSave = () => {
    if (!title.trim()) {
      setTitleError(true);
      return;
    }
    onSave({
      title: title.trim(),
      description: description.trim(),
      status,
      assignee: assignee || undefined,
      dueDate: dueDate || undefined,
      effort: effort ? Number(effort) : undefined,
      recurrence,
      projectId: projectId || undefined,
    });
  };

  return (
    <div
      className="hh-modal-backdrop"
      role="button"
      tabIndex={-1}
      aria-label="Close dialog"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onKeyDown={(e) => {
        if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) onClose();
      }}
    >
      <div
        className="hh-modal"
        role="dialog"
        aria-modal="true"
        aria-label={item ? 'Edit task' : 'New task'}
      >
        <h2>{item ? 'Edit task' : 'New task'}</h2>

        <label className="hh-field" htmlFor="hh-title">
          <span>Title</span>
          <input
            id="hh-title"
            value={title}
            onChange={(e) => { setTitle(e.target.value); setTitleError(false); }}
            placeholder="e.g. Walk the dog"
          />
          {titleError && <span className="hh-field-error">Title is required</span>}
        </label>

        <label className="hh-field" htmlFor="hh-description">
          <span>Description</span>
          <textarea
            id="hh-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
          />
        </label>

        <div className="hh-field-row">
          <label className="hh-field" htmlFor="hh-project">
            <span>Project (optional)</span>
            <select id="hh-project" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
              <option value="">None</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.title}</option>
              ))}
            </select>
          </label>

          <label className="hh-field" htmlFor="hh-status">
            <span>Status</span>
            <select id="hh-status" value={status} onChange={(e) => setStatus(e.target.value as Status)}>
              {(Object.keys(STATUS_LABELS) as Status[]).map((s) => (
                <option key={s} value={s}>{STATUS_LABELS[s]}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="hh-field-row">
          <label className="hh-field" htmlFor="hh-assignee">
            <span>Assignee</span>
            <select
              id="hh-assignee"
              value={assignee}
              onChange={(e) => setAssignee(e.target.value as Person | '')}
            >
              <option value="">Unassigned</option>
              {PEOPLE.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </label>

          <label className="hh-field" htmlFor="hh-due-date">
            <span>Due date</span>
            <input
              id="hh-due-date"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </label>
        </div>

        <div className="hh-field-row">
          <label className="hh-field" htmlFor="hh-effort">
            <span>Effort (optional, 1-5)</span>
            <select id="hh-effort" value={effort} onChange={(e) => setEffort(e.target.value)}>
              <option value="">None</option>
              {[1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>

          <label className="hh-field" htmlFor="hh-recurrence">
            <span>Repeats</span>
            <select
              id="hh-recurrence"
              value={recurrence}
              onChange={(e) => setRecurrence(e.target.value as Recurrence)}
            >
              {(Object.keys(RECURRENCE_LABELS) as Recurrence[]).map((r) => (
                <option key={r} value={r}>{RECURRENCE_LABELS[r]}</option>
              ))}
            </select>
          </label>
        </div>

        {recurrence === 'unspecified' && dueDate && (
          <p className="hh-field-hint">
            This due date applies to this occurrence only — since the repeat has no fixed
            schedule, the next one will come back without a due date.
          </p>
        )}

        <div className="hh-modal-actions">
          <button type="button" className="hh-btn hh-btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="hh-btn hh-btn-primary" onClick={handleSave}>Save</button>
        </div>
      </div>
    </div>
  );
}

export default ItemDialog;
